import { memo, useCallback } from 'react'
import { cn } from '@/utils/cn'

export interface FeedbackButtonsProps {
  messageId: string
  wasHelpful: boolean | null | undefined
  onVote: (messageId: string, wasHelpful: boolean | null) => void
  disabled?: boolean
}

function ThumbsUpIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
    </svg>
  )
}

function ThumbsDownIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.106-1.79l-.05-.025A4 4 0 0011.057 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
    </svg>
  )
}

/**
 * Thumbs up/down feedback buttons for AI messages.
 * Allows users to rate the helpfulness of responses.
 */
export const FeedbackButtons = memo(function FeedbackButtons({
  messageId,
  wasHelpful,
  onVote,
  disabled = false,
}: FeedbackButtonsProps) {
  const up = wasHelpful === true
  const down = wasHelpful === false

  const handleUpvote = useCallback(() => {
    onVote(messageId, up ? null : true)
  }, [messageId, onVote, up])

  const handleDownvote = useCallback(() => {
    onVote(messageId, down ? null : false)
  }, [messageId, onVote, down])

  return (
    <div className="flex items-center gap-1" role="group" aria-label="Rate this response">
      <button
        type="button"
        onClick={handleUpvote}
        disabled={disabled}
        className={cn(
          'rounded p-1 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
          'disabled:pointer-events-none disabled:opacity-50',
          up
            ? 'bg-green-100 text-green-600 hover:bg-green-200'
            : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
        )}
        aria-pressed={up}
        aria-label={up ? 'Remove helpful rating' : 'Mark as helpful'}
      >
        <ThumbsUpIcon className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={handleDownvote}
        disabled={disabled}
        className={cn(
          'rounded p-1 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
          'disabled:pointer-events-none disabled:opacity-50',
          down
            ? 'bg-red-100 text-red-600 hover:bg-red-200'
            : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
        )}
        aria-pressed={down}
        aria-label={down ? 'Remove not helpful rating' : 'Mark as not helpful'}
      >
        <ThumbsDownIcon className="h-4 w-4" />
      </button>
    </div>
  )
})
